import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {DatePipe, NgClass} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {interval} from 'rxjs';
import {MatError, MatFormField, MatHint, MatInput, MatLabel, MatSuffix} from '@angular/material/input';
import {MatButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatDatepicker, MatDatepickerActions, MatDatepickerApply, MatDatepickerCancel, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {MatTimepicker, MatTimepickerInput, MatTimepickerToggle} from '@angular/material/timepicker';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import moment from 'moment';
import {MessageService} from '../../shared/components/message.service';
import {MaintenanceService, MaintenanceWindowDTO} from '../../services/maintenance.service';

const MAINTENANCE_DATE_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
    timeInput: 'HH:mm',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
    timeInput: 'HH:mm',
    timeOptionLabel: 'HH:mm',
  },
};

@Component({
  selector: 'app-maintenance-admin',
  templateUrl: './maintenance-admin.component.html',
  styleUrl: './maintenance-admin.component.scss',
  imports: [
    FormsModule,
    TranslatePipe,
    MatFormField,
    MatInput,
    MatLabel,
    MatError,
    MatHint,
    MatSuffix,
    MatIcon,
    DatePipe,
    NgClass,
    MatButton,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepickerActions,
    MatDatepickerApply,
    MatDatepickerCancel,
    MatTimepicker,
    MatTimepickerInput,
    MatTimepickerToggle,
  ],
  providers: [{provide: MAT_DATE_FORMATS, useValue: MAINTENANCE_DATE_FORMATS}],
  standalone: true,
})
export class MaintenanceAdminComponent implements OnInit {
  maintenanceWindow: MaintenanceWindowDTO = {
    startTime: '',
    endTime: '',
    warningMinutes: 30,
    infoText: '',
    active: false,
    autoLogout: true,
  };
  hasExistingWindow = false;
  showPreview = false;
  confirmingDelete = false;
  saving = false;

  startDateMoment: moment.Moment | null = null;
  startTimeMoment: moment.Moment | null = null;
  endDateMoment: moment.Moment | null = null;
  endTimeMoment: moment.Moment | null = null;

  private destroyRef = inject(DestroyRef);
  now = Date.now();

  constructor(
    private maintenanceService: MaintenanceService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.applyDefaults();
    this.maintenanceService.getWindow().subscribe({
      next: data => {
        this.maintenanceWindow = {...data};
        this.hasExistingWindow = true;
        if (data.startTime) {
          const m = moment(data.startTime);
          this.startDateMoment = m.clone();
          this.startTimeMoment = m.clone();
        }
        if (data.endTime) {
          const m = moment(data.endTime);
          this.endDateMoment = m.clone();
          this.endTimeMoment = m.clone();
        }
      },
      error: err => {
        if (err.status !== 404) {
          this.messageService.error('maintenance.admin.loadError');
        }
      },
    });

    interval(15000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => { this.now = Date.now(); });
  }

  private applyDefaults(): void {
    const afterWarningPeriod = moment().add(this.maintenanceWindow.warningMinutes ?? 30, 'minutes');
    const mins = afterWarningPeriod.minutes();
    let start: moment.Moment;
    if (mins === 0 || mins === 30) {
      start = afterWarningPeriod.clone().seconds(0).milliseconds(0);
    } else if (mins < 30) {
      start = afterWarningPeriod.clone().minutes(30).seconds(0).milliseconds(0);
    } else {
      start = afterWarningPeriod.clone().add(1, 'hour').startOf('hour');
    }
    const end = start.clone().add(1, 'hour');
    this.startDateMoment = start.clone();
    this.startTimeMoment = start.clone();
    this.endDateMoment = end.clone();
    this.endTimeMoment = end.clone();
    this.maintenanceWindow.startTime = start.toISOString();
    this.maintenanceWindow.endTime = end.toISOString();
  }

  onStartDateChange(value: moment.Moment | null): void {
    this.startDateMoment = value;
    this.syncStartTime();
  }

  onStartTimeChange(value: moment.Moment | null): void {
    this.startTimeMoment = value;
    this.syncStartTime();
  }

  onEndDateChange(value: moment.Moment | null): void {
    this.endDateMoment = value;
    this.syncEndTime();
  }

  onEndTimeChange(value: moment.Moment | null): void {
    this.endTimeMoment = value;
    this.syncEndTime();
  }

  private syncStartTime(): void {
    if (!this.startDateMoment) { this.maintenanceWindow.startTime = ''; return; }
    const h = this.startTimeMoment?.hours() ?? 0;
    const min = this.startTimeMoment?.minutes() ?? 0;
    this.maintenanceWindow.startTime = this.startDateMoment.clone().hours(h).minutes(min).seconds(0).toISOString();
  }

  private syncEndTime(): void {
    if (!this.endDateMoment) { this.maintenanceWindow.endTime = ''; return; }
    const h = this.endTimeMoment?.hours() ?? 0;
    const min = this.endTimeMoment?.minutes() ?? 0;
    this.maintenanceWindow.endTime = this.endDateMoment.clone().hours(h).minutes(min).seconds(0).toISOString();
  }

  get phase(): 'off' | 'scheduled' | 'prewarn' | 'running' | 'done' {
    if (!this.maintenanceWindow.active || !this.maintenanceWindow.startTime || !this.maintenanceWindow.endTime) return 'off';
    const start = new Date(this.maintenanceWindow.startTime).getTime();
    const end = new Date(this.maintenanceWindow.endTime).getTime();
    const warn = start - (this.maintenanceWindow.warningMinutes ?? 0) * 60000;
    if (this.now >= end) return 'done';
    if (this.now >= start) return 'running';
    if (this.now >= warn) return 'prewarn';
    return 'scheduled';
  }

  get minsUntilStart(): number {
    return Math.max(0, Math.round((new Date(this.maintenanceWindow.startTime).getTime() - this.now) / 60000));
  }

  get minsUntilEnd(): number {
    return Math.max(0, Math.round((new Date(this.maintenanceWindow.endTime).getTime() - this.now) / 60000));
  }

  get startTimeInvalid(): boolean {
    if (!this.maintenanceWindow.startTime || this.hasExistingWindow) return false;
    return new Date(this.maintenanceWindow.startTime).getTime() <= this.now;
  }

  get endTimeInvalid(): boolean {
    if (!this.maintenanceWindow.endTime || !this.maintenanceWindow.startTime) return false;
    return new Date(this.maintenanceWindow.endTime) <= new Date(this.maintenanceWindow.startTime);
  }

  get warningMinutesInvalid(): boolean {
    return (this.maintenanceWindow.warningMinutes ?? 0) < 5;
  }

  get isFormValid(): boolean {
    if (!this.maintenanceWindow.startTime || !this.maintenanceWindow.endTime || !this.maintenanceWindow.infoText?.trim()) return false;
    if (this.startTimeInvalid || this.endTimeInvalid || this.warningMinutesInvalid) return false;
    return true;
  }

  save(): void {
    if (this.saving) return;
    this.saving = true;
    const payload: MaintenanceWindowDTO = {
      ...this.maintenanceWindow,
      startTime: new Date(this.maintenanceWindow.startTime).toISOString(),
      endTime: new Date(this.maintenanceWindow.endTime).toISOString(),
    };
    this.maintenanceService.saveWindow(payload).subscribe({
      next: () => {
        this.hasExistingWindow = true;
        this.messageService.success('maintenance.admin.saved');
        this.maintenanceService.refreshStatus();
      },
      error: () => this.messageService.error('maintenance.admin.saveError'),
    }).add(() => { this.saving = false; });
  }

  delete(): void {
    if (this.saving) return;
    this.saving = true;
    this.confirmingDelete = false;
    this.maintenanceService.deleteWindow().subscribe({
      next: () => {
        this.hasExistingWindow = false;
        this.maintenanceWindow = {startTime: '', endTime: '', warningMinutes: 30, infoText: '', active: false, autoLogout: true};
        this.applyDefaults();
        this.messageService.success('maintenance.admin.deleted');
        this.maintenanceService.refreshStatus();
      },
      error: () => this.messageService.error('maintenance.admin.deleteError'),
    }).add(() => { this.saving = false; });
  }
}
